import React, { useState } from "react";
import { message } from "antd";

import { MessageInContext } from "@fitness-app/data-models/entities/Chat";
import { type ExerciseRecordingWithExerciseInfo } from "@fitness-app/data-models/entities/ExerciseRecording";

import ChatMessageFormProvider, {
  useChatMessageForm,
} from "~/modules/Chat/components/ChatNotificationContext/ChatMessageFormContext";
import { TraineeVideoRecordingsTable } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/TraineeVideoRecordingsTable";
import { UploadExerciseVideoModal } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/UploadExerciseVideoModal";
import { useTraineeExerciseRecordings } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/useTraineeExerciseRecordings";
import { useUpdateExerciseRecording } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/useUpdateExerciseRecording";
import { supabase } from "~/store/initializeStore";

interface TraineeVideoRecordingsProps {
  traineeId: string;
}

export const TraineeVideoRecordings = ({ traineeId }: TraineeVideoRecordingsProps): React.ReactElement => {
  const { addExerciseRecordingFeedback } = useUpdateExerciseRecording();

  return (
    <ChatMessageFormProvider
      traineeId={traineeId}
      onSuccess={(id, context) => {
        if (context?.type === MessageInContext.AddRecordingFeedback) {
          return addExerciseRecordingFeedback(String(context.metadata.recordingId), id);
        }
      }}
    >
      <TraineeVideoRecordingsInner traineeId={traineeId} />
    </ChatMessageFormProvider>
  );
};

export const TraineeVideoRecordingsInner = ({ traineeId }: { traineeId: string }) => {
  const { data, isLoading } = useTraineeExerciseRecordings(traineeId);
  const { open } = useChatMessageForm();
  const { archiveExerciseRecording, deleteExerciseRecordingVideo } = useUpdateExerciseRecording();
  const [uploadModalRecording, setUploadModalRecording] = useState<ExerciseRecordingWithExerciseInfo | null>(null);

  const handleDeleteVideo = async (recording: ExerciseRecordingWithExerciseInfo) => {
    const path = recording.metadata?.path;
    await deleteExerciseRecordingVideo(recording);
    if (path) {
      const { error } = await supabase.storage.from("users").remove([path]);
      if (error) {
        message.error(`Błąd podczas usuwania filmu: ${error.message}`);
      }
    }
  };

  const handleArchiveRecording = async (recording: ExerciseRecordingWithExerciseInfo) => {
    await archiveExerciseRecording(recording);
    message.success("Nagranie zostało zarchiwizowane.");
  };

  return (
    <>
      <TraineeVideoRecordingsTable
        data={data || []}
        isLoading={isLoading}
        onAddFeedback={(recording) => {
          open({
            type: MessageInContext.AddRecordingFeedback,
            metadata: {
              exerciseName: recording.exercise.name,
              recordingId: recording.id,
              url: recording.url,
              workoutId: recording.workoutId,
              exerciseDate: recording.scheduledAt,
            },
          });
        }}
        onUploadVideo={(recording) => setUploadModalRecording(recording)}
        onDeleteVideo={handleDeleteVideo}
        onArchiveRecording={handleArchiveRecording}
      />
      <UploadExerciseVideoModal
        visible={!!uploadModalRecording}
        recording={uploadModalRecording}
        traineeId={traineeId}
        onClose={() => setUploadModalRecording(null)}
      />
    </>
  );
};
